import { EventNames, getAnonymousId, identify, track, resetPosthog } from 'utils/analytics.utils';
import { reportError } from 'utils/sentry.utils';
import { getRouterLocation, navigate } from 'services/router-service';
import { retryStrategies } from '@env0/common-retry';

import { auth0Audience, auth0ClientId, auth0Domain, auth0LoginCallbackUrl, stage } from 'constants/config';

import { action, observable } from 'mobx';

import BaseService from 'services/base-service';
import BrowserStorage from 'services/BrowserStorage';
import { WebAuth, type Auth0UserProfile } from 'auth0-js';
import type ServiceContainer from 'services/service-container';
import Cookies from 'js-cookie';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';
import { NotificationTypes } from 'types/notifications.types';

export interface AuthResult {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  idTokenPayload: Record<string, any>;
  state?: string;
  tokenType?: string;
}

export const AUTH0_RETRY_SETTINGS = {
  maxAttempts: 3,
  initialDelay: 200,
  retryPolicy: {
    fieldName: 'code',
    cancelOnErrors: ['login_required']
  }
};

const neededKeysInUrlHash = /access_token|id_token/;
// set by the Auth0 post-login action (redirect-social-login-to-sso) when a social login belongs to an SSO org
const ssoRequired = 'sso_required';
const ssoRequiredMarker = new RegExp(`^${ssoRequired}:([\\w-]+)$`);

export const redirectAfterLoginKey = 'env0redirectAfterLogin';
export const browserStorageKeys = {
  authResult: 'authResult',
  expiresAt: 'expiresAt',
  ssoEmail: 'ssoEmail',
  userProfile: 'userProfile'
};

const SESSION_CHECK_INTERVAL = 245 * 60 * 1000; // 245 minutes (a bit more than 4 hours, Visa's session timeout)

type Auth0RedirectError = {
  error?: string | null;
  errorDescription?: string | null;
};

class AuthenticatedFailed extends Error {
  constructor(authError: { error: string; errorDescription: string }) {
    super(`Authentication Failed: ${authError?.errorDescription} [${authError?.error}]`);
  }
}

export class AuthStore extends BaseService {
  @observable private accessToken: string | null = null;

  @observable public isAuthenticated = false;
  rememberLastLogin = true;

  private lastSessionCheckTime: number | null = null;

  private isHandlingUniversalLoginCallback = false;
  private isRedirectingToUniversalLogin = false;

  private webAuth = new WebAuth({
    domain: `${auth0Domain}`,
    clientID: `${auth0ClientId}`,
    redirectUri: auth0LoginCallbackUrl + 'login',
    responseType: 'token id_token'
  });

  private getAuth0RedirectErrorFromLocation(): Auth0RedirectError | null {
    const searchParams = new URLSearchParams(window.location.search);

    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error || errorDescription) {
      return { error, errorDescription };
    }

    const hash = window.location.hash.substring(1);
    if (!hash) return null;

    const hashParams = new URLSearchParams(hash);
    const hashError = hashParams.get('error');
    const hashErrorDescription = hashParams.get('error_description');

    if (hashError || hashErrorDescription) {
      return { error: hashError, errorDescription: hashErrorDescription };
    }

    return null;
  }

  private clearAuth0RedirectErrorFromLocation() {
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');

    // Some flows might include error details in the fragment as well.
    const hash = url.hash.startsWith('#') ? url.hash.substring(1) : url.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      hashParams.delete('error');
      hashParams.delete('error_description');
      const newHash = hashParams.toString();
      url.hash = newHash ? `#${newHash}` : '';
    }

    const nextPath = url.pathname + (url.search ? url.search : '') + (url.hash ? url.hash : '');
    void navigate(nextPath, { replace: true });
  }

  private getUserFriendlyAuth0RedirectErrorMessage(authError: Auth0RedirectError): string {
    const description = authError.errorDescription || '';
    const error = authError.error || '';
    const haystack = `${error}, ${description}`.toLowerCase();

    if (includes(haystack, 'invalid client secret') || includes(haystack, 'client secret')) {
      return 'SSO login failed because the SSO configuration is invalid or expired (invalid client secret). Please contact your administrator.';
    }

    if (includes(haystack, 'certificate') || includes(haystack, 'x509') || includes(haystack, 'thumbprint')) {
      return 'SSO login failed because the SSO configuration is invalid or expired (certificate issue). Please contact your administrator.';
    }

    // bare marker: several SSO connections claim the domain, so the action could not pick one to forward to
    if (description.startsWith(ssoRequired)) {
      return 'login.sso.required';
    }

    return haystack || 'Login failed';
  }

  hasAuth0RedirectErrorInUrl = () => {
    return !isNil(this.getAuth0RedirectErrorFromLocation());
  };

  @action
  handleAuth0RedirectErrorFromUrl = (): string | null => {
    const authError = this.getAuth0RedirectErrorFromLocation();
    if (!authError) return null;

    const message = this.getUserFriendlyAuth0RedirectErrorMessage(authError);

    reportError(
      'Authentication',
      new AuthenticatedFailed({
        error: authError.error || 'auth0_redirect_error',
        errorDescription: authError.errorDescription || 'Auth0 redirect returned an error'
      })
    );

    this.clearAuth0RedirectErrorFromLocation();

    return message;
  };

  @action
  redirectToSsoIfRequired = (): boolean => {
    const authError = this.getAuth0RedirectErrorFromLocation();
    // only the action's deny carries the marker; anything else with that text is not ours to act on
    if (authError?.error !== 'access_denied') return false;
    const connection = ssoRequiredMarker.exec(authError.errorDescription ?? '')?.[1];
    if (!connection) return false;

    this.clearAuth0RedirectErrorFromLocation();
    this.isRedirectingToUniversalLogin = true;
    void (async () => {
      try {
        await this.redirectToUniversalLogin(await getAnonymousId(), { connection });
      } catch (error) {
        // the url error is already cleared, so surface the failure instead of leaving a blank page
        this.isRedirectingToUniversalLogin = false;
        this.onAuthorizationError(error);
      }
    })();

    return true;
  };

  constructor(service: ServiceContainer) {
    super(service);
  }

  get lastTokenExpirationDate(): number | null {
    const expiresAt = BrowserStorage.getItem(browserStorageKeys.expiresAt);
    return expiresAt ? parseInt(expiresAt) : null;
  }

  get lastAccessTokenIsValid(): boolean {
    return this.lastTokenExpirationDate ? new Date().getTime() < this.lastTokenExpirationDate : false;
  }

  @action
  async afterSuccessLogin(authResult: AuthResult) {
    this.setSessionCheckTime();
    this.storeLoginData(authResult);
    this.accessToken = authResult.accessToken;
    await this.loadUserData(authResult);
    this.isAuthenticated = true;

    this.rememberLastLogin = true;

    console.log('Succeed to Auth Login');
  }

  @action
  async login() {
    try {
      let authData = BrowserStorage.getItem(browserStorageKeys.authResult);

      if (isNil(authData) || !this.lastAccessTokenIsValid) {
        authData = await this.getAuth0Session();
        this.setSessionCheckTime();
        this.storeLoginData(authData);
      }

      this.accessToken = authData.accessToken;
      await this.loadUserData(authData);
      this.isAuthenticated = true;

      console.log('Succeed to Login');
    } catch (error) {
      console.log('Failed to login', error);
      this.clearSessionCheckTime();
      this.deleteLoginData();
      this.navigateToLogin();
    }
  }

  async loadUserData(authResult: AuthResult) {
    await this.service.organizationsStore.acceptOrganizationInvitationIfExists();
    const info = await this.getUserInfo();
    info?.email && BrowserStorage.setItem(browserStorageKeys.ssoEmail, info.email);
    this.trackSignup(authResult);
  }

  @action
  async silentRenewSession() {
    try {
      // In E2E mode, request fresh tokens from Cypress (no Auth0 session cookie available)
      const isE2E = localStorage.getItem('e2e-mode') === 'true';
      const authData = isE2E ? await this.getE2ESession() : await this.getAuth0Session();

      this.setSessionCheckTime();
      this.storeLoginData(authData);
      this.accessToken = authData.accessToken;
      this.isAuthenticated = true;

      console.log('Succeed to renewSession');
    } catch (error) {
      console.log('Failed to renewSession', error);
      this.clearSessionCheckTime();
      this.deleteLoginData();
      this.navigateToLogin();
    }
  }

  private getE2ESession(): Promise<AuthResult> {
    console.info('Requesting E2E session refresh from Cypress');

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('E2E token refresh timeout')), 10000);

      const handler = (event: CustomEvent<AuthResult>) => {
        clearTimeout(timeout);
        window.removeEventListener('e2e-token-response', handler as EventListener);
        resolve(event.detail);
      };

      window.addEventListener('e2e-token-response', handler as EventListener);
      window.dispatchEvent(new CustomEvent('e2e-token-request'));
    });
  }

  /**
   * Checks if the session is still valid.
   * Should check every 30 minutes to avoid unnecessary calls to Auth0.
   * If the session is expired, it will redirect to the login page (through silentRenewSession)
   */
  @action
  async checkSession() {
    // If the session check is too old, we need to do silent renew
    if (this.lastSessionCheckTime && Date.now() - this.lastSessionCheckTime > SESSION_CHECK_INTERVAL) {
      await this.silentRenewSession();
    }
  }

  navigateToLogin() {
    const { pathname, search, hash } = getRouterLocation();
    if (pathname === '/login') return;
    this.setRedirectToUrl(pathname + search + hash);
    void navigate({ pathname: '/login', search: '', hash: '' });
  }

  private setSessionCheckTime() {
    this.lastSessionCheckTime = Date.now();
  }

  private clearSessionCheckTime() {
    this.lastSessionCheckTime = null;
  }

  private storeLoginData(authResult: AuthResult) {
    const expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
    BrowserStorage.setItem(browserStorageKeys.expiresAt, expiresAt);
    BrowserStorage.setItem(browserStorageKeys.authResult, authResult);

    console.info('Store Auth data', { authResult, expiresAt });
  }

  private deleteLoginData() {
    BrowserStorage.removeItem(browserStorageKeys.expiresAt);
    BrowserStorage.removeItem(browserStorageKeys.authResult);
    BrowserStorage.removeItem(browserStorageKeys.userProfile);
    this.accessToken = null;
    this.isAuthenticated = false;
    console.info('clear Auth data');
  }

  private async getAuth0Session(): Promise<AuthResult> {
    try {
      console.info('Checking user session');

      return await retryStrategies.withExponentialBackoff(
        () =>
          new Promise<AuthResult>((resolve, reject) => {
            this.webAuth.checkSession(
              {
                responseType: 'token id_token',
                scope: 'openid profile email',
                audience: auth0Audience
              },
              (error, result: AuthResult | undefined) => {
                if (error) {
                  console.log('Got error from auth0 checkSession', error);
                  return reject(error);
                }
                return resolve(result as AuthResult);
              }
            );
          }),
        AUTH0_RETRY_SETTINGS
      );
    } finally {
      this.clearLeftoverAuth0Cookies(); // After every Auth request, we need to clear the auth0 cookies
    }
  }

  private fetchUserInfo = (accessToken: string): Promise<Auth0UserProfile> => {
    console.info('Fetching user info');
    return retryStrategies.withExponentialBackoff(
      () =>
        new Promise((resolve, reject) => {
          this.webAuth.client.userInfo(accessToken, (error: any, profile: Auth0UserProfile) =>
            error ? reject(new Error(`Error fetching auth0 user data`, { cause: error })) : resolve(profile)
          );
        }),
      AUTH0_RETRY_SETTINGS
    );
  };

  private clearLeftoverAuth0Cookies() {
    const cookies = Cookies.get();
    const auth0CookieKeys = Object.keys(cookies).filter(key => key.includes('com.auth0.auth.'));

    track('AUTH0_COOKIES_COUNT', { cookiesCount: auth0CookieKeys.length, cookiesLength: document.cookie.length });
    // Remove all auth0 cookies except the last two
    auth0CookieKeys.slice(0, -2).forEach(key => Cookies.remove(key));
  }

  public getAccessToken() {
    return this.accessToken;
  }

  setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
  }

  onAuthorizationError = (error: any) => {
    reportError('Authentication', new AuthenticatedFailed(error));
    this.rememberLastLogin = false;

    this.service.notificationStore.setNotification({
      notificationType: NotificationTypes.error,
      message: error?.errorDescription || error?.message
    });
  };

  trackSignup = (result: AuthResult) => {
    const { idTokenPayload } = result;
    if ((idTokenPayload as any)['https://env0.com/is_first_login']) {
      track(EventNames.SIGNUP);
    }
  };

  getCachedUserProfile = () => {
    const storedProfile = localStorage.getItem(browserStorageKeys.userProfile);
    if (!storedProfile) return null;

    const { data, expiry } = JSON.parse(storedProfile) || {};
    if (Date.now() < expiry) return data;

    localStorage.removeItem(browserStorageKeys.userProfile);
    return null;
  };

  getUserInfo = async () => {
    try {
      const accessToken = this.accessToken;

      if (!accessToken) {
        return;
      }

      let profile = this.getCachedUserProfile();

      if (!profile) {
        profile = await this.fetchUserInfo(accessToken);
        localStorage.setItem(
          browserStorageKeys.userProfile,
          JSON.stringify({
            data: profile,
            expiry: Date.now() + 5 * 60 * 1000
          })
        );
      }

      this.service.userStore.setProfile(profile);

      identify(this.service.userStore.userId as string, profile);
      await this.service.organizationsStore.getOrganizations();
      return profile;
    } catch (error) {
      console.error('Failed to get user info', { cause: error });
      throw error;
    } finally {
      this.clearLeftoverAuth0Cookies();
    }
  };

  logout = (message?: string, returnTo?: string) => {
    resetPosthog();
    const returnToUrl = returnTo || auth0LoginCallbackUrl;
    const returnToWithMessage = message ? `${returnToUrl}?logout_reason=${encodeURI(message)}` : returnToUrl;
    console.info(`Logging out, redirecting to ${returnToWithMessage}`);
    this.clearSessionCheckTime();
    this.deleteLoginData();
    this.webAuth.logout({
      returnTo: returnToWithMessage
    });
  };

  isCallbackPage() {
    return neededKeysInUrlHash.test(window.location.hash);
  }

  private generateRandomUrlSafeString = (length: number) => {
    try {
      const bytes = new Uint8Array(length);
      crypto.getRandomValues(bytes);
      return btoa(String.fromCharCode(...Array.from(bytes)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
        .slice(0, length);
    } catch (error) {
      return Math.random()
        .toString(36)
        .slice(2, 2 + length);
    }
  };

  redirectToUniversalLogin = async (anonymousId: string | null, extra: { connection?: string } = {}) => {
    const authParams = this.getAuthParams(anonymousId);
    const extraParams: Record<string, string> = { ...extra };

    if (authParams.stage) extraParams.stage = authParams.stage;
    if (authParams.anonymousId) extraParams.anonymousId = authParams.anonymousId;
    if (authParams.user_data) extraParams.user_data = JSON.stringify(authParams.user_data);

    this.webAuth.authorize({
      redirectUri: auth0LoginCallbackUrl + 'login',
      responseType: 'token id_token',
      responseMode: 'fragment',
      scope: 'openid profile email',
      audience: auth0Audience,
      ...extraParams
    });
  };

  /**
   * Parses the authentication tokens directly from the URL hash fragment.
   *
   * We bypass Auth0's parseHash() method because it performs state validation that fails
   * with Universal Login. When using Auth0 Universal Login (hosted login page), the OAuth
   * state is managed by Auth0's hosted page rather than our client-side SDK. This causes
   * a "state does not match" error when parseHash() tries to validate the state parameter.
   *
   * By extracting tokens directly from the hash, we avoid this validation issue while still
   * properly authenticating the user. The tokens themselves are still validated by Auth0's
   * backend when we make API calls.
   */
  private decodeBase64Url(str: string): string {
    // Convert base64url to base64 by replacing URL-safe characters
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // Pad with '=' to make length a multiple of 4
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return atob(padded);
  }

  private parseHashFragment(): AuthResult | null {
    const hash = window.location.hash.substring(1);
    if (!hash) return null;

    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const idToken = params.get('id_token');
    const expiresIn = params.get('expires_in');

    if (!accessToken || !idToken) return null;

    const payload = idToken.split('.')[1];
    const decodedPayload = this.decodeBase64Url(payload);

    return {
      accessToken,
      idToken,
      expiresIn: expiresIn ? parseInt(expiresIn, 10) : 86400,
      idTokenPayload: JSON.parse(decodedPayload)
    } as AuthResult;
  }

  @action
  handleUniversalLoginCallback = async () => {
    if (!this.isCallbackPage() || this.isAuthenticated) return;
    if (this.isHandlingUniversalLoginCallback) return;

    this.isHandlingUniversalLoginCallback = true;

    try {
      // Extract tokens directly from hash to avoid state validation issues with Universal Login
      const authResult = this.parseHashFragment();
      if (!authResult) {
        throw new Error('Failed to parse authentication result from URL');
      }

      // Clear the hash after extracting tokens
      await navigate(
        { pathname: window.location.pathname, search: window.location.search, hash: '' },
        { replace: true }
      );

      this.setSessionCheckTime();
      this.storeLoginData(authResult);
      this.accessToken = authResult.accessToken;
      await this.loadUserData(authResult);
      this.isAuthenticated = true;
    } catch (error: any) {
      console.error(error);
      this.onAuthorizationError(error);
    } finally {
      this.isHandlingUniversalLoginCallback = false;
      this.isRedirectingToUniversalLogin = false;
    }
  };

  @action
  show = async () => {
    if (this.isRedirectingToUniversalLogin) return;
    this.isRedirectingToUniversalLogin = true;

    const anonymousId = await getAnonymousId();
    await this.redirectToUniversalLogin(anonymousId);
  };

  getRedirectToUrl = () => {
    return BrowserStorage.getItem(redirectAfterLoginKey) || '/';
  };

  setRedirectToUrl = (pathname: string) => {
    if (!pathname?.startsWith('/login')) BrowserStorage.setItem(redirectAfterLoginKey, pathname);
  };

  getAuthParams = (anonymousId: string | null = null) => {
    return {
      scope: 'openid profile email',
      stage,
      anonymousId: anonymousId ?? undefined,
      user_data: this.getUserData()
    };
  };

  getUserData = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const userData = searchParams.get('userData');
    let returnValue;
    if (userData) {
      try {
        returnValue = JSON.parse(atob(userData));
      } catch (exception) {
        console.error(`Failed to parse userData = ${userData}`);
      }
    }
    return returnValue;
  };
}
